import SearchInput from '@shared/components/SearchInput';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Caption1,
  Dropdown,
  Option,
  OptionOnSelectData,
  SelectionEvents,
} from '@fluentui/react-components';
import { AddFilled, PeopleRegular } from '@fluentui/react-icons';

import { ConfirmDialog, ListPageLayout, Pagination, Path, StatusLabel, TConfirmDialogState } from '@shared/components';
import { calculateRowsPerPage } from '@shared/helpers';
import { useFolderPathAsBreadcrumbs, usePageClamp, useSearchParams } from '@shared/hooks';
import { TERMINOLOGY } from '@shared/i18n';
import { useRegisterAiAssistantOrigin } from '@modules/ai/hooks/useRegisterAiAssistantOrigin.hook';
import { EAiAssistantOriginItemType } from '@modules/ai/types';
import { AccessDialog, TAccessDialogState, AccessType, useCanEdit } from '@modules/access';
import { EFolderType, FoldersNavDrawer, TFolder, TPath, useProductFolders } from '@modules/items';

import { useArchiveLiterature, useLiteratures } from '../api';
import { EAssessmentDecision, ELiteratureType, EReviewingStage, TLiterature } from '../types';
import {
  DECISION_OPTIONS,
  LITERATURES_UI_MARGIN_TOP,
  PRODUCT_IDS_SEARCH_PARAM,
  REVIEWING_STAGE_OPTIONS,
  TYPE_OPTIONS,
} from '../constants';
import { useProductIdsSearchParam } from '../hooks/useProductIdsSearchParam.hook';
import LiteratureTable from './LiteratureTable';
import LiteratureDialog from './dialogs/LiteratureDialog';
import ProductPicker from './ProductPicker';

type TProps = {
  qmFolder: TFolder;
  literatureFolder: TFolder;
};

const LITERATURE_NAV_ITEM = Object.freeze({
  _id: 'literatures',
  name: TERMINOLOGY.literature.upper,
  accessRight: 0,
});

const LiteraturesContent = ({ qmFolder, literatureFolder }: TProps) => {
  useRegisterAiAssistantOrigin({
    item: literatureFolder._id,
    itemType: EAiAssistantOriginItemType.LITERATURE_MANAGEMENT,
  });

  const navigate = useNavigate();

  // Literature lives under QM in the nav (future "global workspace"), so the
  // drawer / breadcrumbs are driven by `qmFolder`. `literatureFolder` is used
  // for the backend-enforced EDIT/MANAGE checks (Create, Access) instead.

  const { searchParams, change, changeMany } = useSearchParams();
  const page = Number(searchParams.get('page'));
  const pageSize = Number(searchParams.get('pageSize'));
  const search = searchParams.get('search') ?? '';
  const typeParam = searchParams.get('type');
  // `decision` and `type` arrive from the URL, so they can hold any string a
  // user pastes in. Validate against the known enum values before passing
  // them to the API or React Query keys.
  const rawDecision = searchParams.get('decision');
  const decision =
    rawDecision && DECISION_OPTIONS.some((option) => option.value === rawDecision)
      ? (rawDecision as EAssessmentDecision)
      : undefined;

  const rawReviewingStage = searchParams.get('reviewingStage');
  const reviewingStage =
    rawReviewingStage && (Object.values(EReviewingStage) as string[]).includes(rawReviewingStage)
      ? (rawReviewingStage as EReviewingStage)
      : undefined;

  const productIds = useProductIdsSearchParam();

  const typeFilter: ELiteratureType[] | undefined = useMemo(() => {
    if (!typeParam) return undefined;
    const known = new Set<string>(Object.values(ELiteratureType));
    const parsed = typeParam.split(',').filter((v) => known.has(v)) as ELiteratureType[];
    return parsed.length ? parsed : undefined;
  }, [typeParam]);

  const productFolders = useProductFolders();
  const products = useMemo(() => productFolders.map((item) => ({ _id: item._id, name: item.name })), [productFolders]);

  // Without this, Fluent shows the placeholder even when options are selected.
  const typeDisplay = useMemo(() => {
    if (!typeFilter?.length) return '';
    const labels = typeFilter.map((v) => TYPE_OPTIONS.find((o) => o.value === v)?.label).filter(Boolean) as string[];
    const head = labels.slice(0, 2).join(', ');
    return head + (labels.length > 2 ? ', ...' : '');
  }, [typeFilter]);

  useEffect(() => {
    const patch: Record<string, string | undefined> = {};
    if (Number(page) < 1) patch.page = '1';
    if (Number(pageSize) < 1) patch.pageSize = String(calculateRowsPerPage(LITERATURES_UI_MARGIN_TOP));
    // Batch into a single navigate so page/pageSize land together (sequential changes caused a brief
    // visual swap of the two params in the URL bar).
    if (Object.keys(patch).length > 0) changeMany(patch);
  }, [page, pageSize]);

  const { data, isLoading, error } = useLiteratures({
    page,
    pageSize,
    search: search || undefined,
    type: typeFilter,
    productIds,
    decision: productIds.length > 0 ? decision : undefined,
    reviewingStage: productIds.length > 0 ? reviewingStage : undefined,
  });

  usePageClamp(data, isLoading);

  // Creating needs EDIT on the literature folder, not on the route folder.
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { canEdit } = useCanEdit(literatureFolder.accessRight);

  const [accessDialogState, setAccessDialogState] = useState<TAccessDialogState | undefined>(undefined);
  const canManageAccess = literatureFolder.accessRight >= AccessType.MANAGE;

  const archiveLiterature = useArchiveLiterature();
  const [confirmDialogState, setConfirmDialogState] = useState<TConfirmDialogState | undefined>(undefined);
  const handleArchive = (item: TLiterature) => {
    const displayTitle = item.draft?.content.title ?? item.versions[0]?.content.title ?? '';
    const dialogState: TConfirmDialogState = {
      isOpen: true,
      title: `Archive ${TERMINOLOGY.literature.lower}?`,
      content: `"${displayTitle}" will be moved to the archive.`,
      buttonLabel: 'Archive',
      danger: true,
      onConfirm: async () => {
        await archiveLiterature.mutateAsync(item._id);
        setConfirmDialogState({ ...dialogState, isOpen: false });
      },
    };
    setConfirmDialogState(dialogState);
  };

  // Literature lives under QM in the nav, so the breadcrumb root is qmFolder.
  const fullPath: TPath = [
    { _id: qmFolder._id, name: qmFolder.name, accessRight: qmFolder.accessRight },
    LITERATURE_NAV_ITEM,
  ];

  return (
    <ListPageLayout nav={<FoldersNavDrawer folder={qmFolder} fullPath={fullPath} selectedValue="literatures" />}>
      <div className="flex flex-col space-y-5 pt-4 pb-16 pl-5 pr-8 max-w-screen-2xl">
        <Path items={useFolderPathAsBreadcrumbs(fullPath)} hasCurrent size="large" />

        {(canEdit || canManageAccess) && (
          <div className="flex gap-2">
            {canEdit && (
              <Button
                appearance="primary"
                size="medium"
                icon={<AddFilled />}
                onClick={() => setIsCreateDialogOpen(true)}
                className="w-fit"
              >
                New
              </Button>
            )}

            {canManageAccess && (
              <Button
                appearance="secondary"
                size="medium"
                icon={<PeopleRegular />}
                onClick={() =>
                  setAccessDialogState({
                    isOpen: true,
                    id: literatureFolder._id,
                    nodeType: 'FOLDER',
                    name: literatureFolder.name,
                  })
                }
                className="w-fit"
              >
                Access
              </Button>
            )}
          </div>
        )}

        <div className="flex flex-col gap-3">
          {/* Literature filters - scope the list itself. */}
          <div className="flex items-center gap-3 flex-wrap">
            <Caption1 className="w-24 text-neutral-500 shrink-0">Literature</Caption1>
            <SearchInput
              placeholder="Title, author, reference number, DOI..."
              value={search}
              onChange={(value) => changeMany({ search: value || undefined, page: '1' })}
              className="flex-1 min-w-56 max-w-96"
            />

            <Dropdown
              placeholder="Article type"
              multiselect
              selectedOptions={typeFilter ?? []}
              value={typeDisplay}
              onOptionSelect={(_: SelectionEvents, d: OptionOnSelectData) => {
                const selected = d.selectedOptions;
                changeMany({ type: selected.length ? selected.join(',') : undefined, page: '1' });
              }}
              className="w-52"
            >
              {TYPE_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Dropdown>

            {/* Hidden for product-folder deep-links - productIds is implicit and clearing it would break context. */}
            {qmFolder.folderType !== EFolderType.PRODUCT && (
              <ProductPicker
                products={products}
                selectedIds={productIds}
                onChange={(next) => {
                  const hasScope = next.length > 0;
                  changeMany({
                    [PRODUCT_IDS_SEARCH_PARAM]: next.length ? next : undefined,
                    // Clear assessment-scoped filters when clearing products - they're meaningless without scope.
                    decision: hasScope ? (decision ?? undefined) : undefined,
                    reviewingStage: hasScope ? (reviewingStage ?? undefined) : undefined,
                    page: '1',
                  });
                }}
                placeholder="All products"
                variant="summary"
                className="w-80"
              />
            )}
          </div>

          {/* Assessment filters - only meaningful when scoped to a product. */}
          <div className="flex items-center gap-3 flex-wrap">
            <Caption1 className="w-24 text-neutral-500 shrink-0">Assessment</Caption1>
            <Dropdown
              placeholder="Decision"
              disabled={productIds.length === 0}
              selectedOptions={decision ? [decision] : ['']}
              value={decision ? (DECISION_OPTIONS.find((o) => o.value === decision)?.label ?? '') : ''}
              onOptionSelect={(_: SelectionEvents, d: OptionOnSelectData) => {
                const val = d.selectedOptions[0] as EAssessmentDecision | undefined;
                changeMany({ decision: val || undefined, page: '1' });
              }}
              className="w-44"
            >
              <Option value="" text="All decisions">
                All decisions
              </Option>
              {DECISION_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value} text={opt.label}>
                  {opt.label}
                </Option>
              ))}
            </Dropdown>

            <Dropdown
              placeholder="Reviewing stage"
              disabled={productIds.length === 0}
              selectedOptions={reviewingStage ? [reviewingStage] : ['']}
              value={
                reviewingStage ? (REVIEWING_STAGE_OPTIONS.find((o) => o.value === reviewingStage)?.label ?? '') : ''
              }
              onOptionSelect={(_: SelectionEvents, d: OptionOnSelectData) => {
                const val = d.selectedOptions[0] as EReviewingStage | undefined;
                changeMany({ reviewingStage: val || undefined, page: '1' });
              }}
              className="w-52"
            >
              <Option value="" text="All stages">
                All stages
              </Option>
              {REVIEWING_STAGE_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value} text={opt.label}>
                  {opt.label}
                </Option>
              ))}
            </Dropdown>
          </div>
        </div>

        {/* Assessment counts (only when scoped to a single product - counts are per-product and
                backend omits them otherwise). Badge colors match the canonical StatusBadge:
                brand=positive, danger, warning, informative=neutral. */}
        {data?.assessmentCounts && (
          <div className="flex space-x-2">
            <Badge appearance="tint" color="brand">
              Included: {data.assessmentCounts.included}
            </Badge>
            <Badge appearance="tint" color="danger">
              Excluded: {data.assessmentCounts.excluded}
            </Badge>
            <Badge appearance="tint" color="warning">
              Pending: {data.assessmentCounts.pending}
            </Badge>
          </div>
        )}

        {/* StatusLabel handles the loading / error / empty cycle with a delayed spinner
                (no flash on fast loads) - same convention used across the rest of the app. */}
        <StatusLabel
          data={data}
          error={error}
          loadingMessage={`Loading ${TERMINOLOGY.literature.lower}...`}
          errorMessage={`Failed to load ${TERMINOLOGY.literature.lower} list!`}
          render={(loaded) =>
            loaded.items.length > 0 ? (
              <>
                <LiteratureTable
                  items={loaded.items}
                  productId={productIds.length === 1 ? productIds[0] : undefined}
                  canEdit={canEdit}
                  onArchive={handleArchive}
                />
                <Pagination total={loaded.total} margin={LITERATURES_UI_MARGIN_TOP} showTotal />
              </>
            ) : (
              <p className="text-neutral-500 py-8">No {TERMINOLOGY.literature.lower} found.</p>
            )
          }
        />

        {/* Create dialog - always mounted */}
        <LiteratureDialog
          isOpen={isCreateDialogOpen}
          setIsOpen={setIsCreateDialogOpen}
          onCreated={(created) => navigate(`/literature/${created._id}`)}
        />
        <ConfirmDialog state={confirmDialogState} setState={setConfirmDialogState} />
        <AccessDialog state={accessDialogState} setState={setAccessDialogState} />
      </div>
    </ListPageLayout>
  );
};

export default LiteraturesContent;
